import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import apiService from "../services/api";
import { auth } from "../firebaseClient";
import analytics, { errorCode } from "../services/analytics";
import "../styles.css";

const friendlyFirebaseError = (code) => {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Incorrect email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists. Try signing in instead.";
    case "auth/invalid-email":
      return "That doesn't look like a valid email.";
    case "auth/weak-password":
      return "Password is too weak — use at least 6 characters with one uppercase letter and one number.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return null;
  }
};

// Password rules — kept here so the UI checklist and the submit guard read from
// the same source of truth. Adjust thresholds in one place.
const passwordRules = [
  { id: "length", label: "At least 6 characters", test: (p) => p.length >= 6 },
  { id: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "digit", label: "One number", test: (p) => /\d/.test(p) },
];

const AuthPage = () => {
  const [mode, setMode] = useState("login"); // "login" | "register" | "reset"
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const loginAttemptRef = useRef(false);

  useEffect(() => {
    // If we're already signed in (cached or live), route appropriately.
    const unsubscribe = apiService.onAuthChange((profile) => {
      if (!profile || !apiService.hasResolved()) return;
      if (loginAttemptRef.current) {
        loginAttemptRef.current = false;
        analytics.capture("login_succeeded");
      }
      if (profile.role === "EXPERT") {
        navigate("/dashboard");
      } else {
        navigate("/pending");
      }
    });
    const unsubErr = apiService.onSyncError((err) => {
      loginAttemptRef.current = false;
      setIsLoading(false);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Couldn't reach the backend after sign-in.";
      setError(
        `Signed in to Firebase, but the server rejected the session: ${msg}. ` +
          `Check the backend terminal for details.`,
      );
    });
    return () => {
      unsubscribe();
      unsubErr();
    };
  }, [navigate]);

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setSuccessMessage("");
    setPassword("");
    setConfirmPassword("");
  };

  // Live password checks — recomputed on every keystroke, drives both the
  // checklist UI and the submit button's disabled state.
  const passwordChecks = passwordRules.map((r) => ({
    ...r,
    passed: r.test(password),
  }));
  const allPasswordRulesMet = passwordChecks.every((c) => c.passed);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmitRegister =
    !isLoading &&
    username.trim().length >= 3 &&
    email.trim().length > 0 &&
    allPasswordRulesMet &&
    passwordsMatch;

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }
    setIsLoading(true);
    setError("");
    loginAttemptRef.current = true;
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthChange listener handles navigation once /api/auth/sync resolves.
    } catch (err) {
      loginAttemptRef.current = false;
      analytics.capture("login_failed", { error_code: errorCode(err) });
      setIsLoading(false);
      setError(friendlyFirebaseError(err.code) || "Login failed. Please try again.");
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    // Belt and suspenders — the button is disabled when canSubmitRegister is
    // false, but a determined user can still trigger form submit via Enter.
    if (!canSubmitRegister) {
      if (!allPasswordRulesMet) {
        setError("Password doesn't meet all requirements yet.");
      } else if (!passwordsMatch) {
        setError("Passwords do not match.");
      } else {
        setError("Please fill in all fields.");
      }
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    let accountCreated = false;
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      accountCreated = true;
      analytics.capture("registration_succeeded");
      await updateProfile(cred.user, { displayName: username });
      setSuccessMessage("Account created. Waiting for approval...");
      // onAuthChange routes us to /pending automatically (new users are PUBLIC).
    } catch (err) {
      if (!accountCreated) analytics.capture("registration_failed", { error_code: errorCode(err) });
      setIsLoading(false);
      setError(friendlyFirebaseError(err.code) || "Registration failed. Please try again.");
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSuccessMessage(
        "If an account exists for that email, a password reset link has been sent. Check your inbox (and spam folder).",
      );
    } catch (err) {
      // Firebase returns the same code for "user not found" — we deliberately
      // show a generic success message either way to avoid leaking which
      // emails are registered. Only surface obvious failures (network, etc.).
      if (err.code === "auth/user-not-found") {
        setSuccessMessage(
          "If an account exists for that email, a password reset link has been sent. Check your inbox (and spam folder).",
        );
      } else {
        setError(friendlyFirebaseError(err.code) || "Couldn't send reset email. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => navigate("/");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Digitomy Trap Annotate
          </motion.h1>
          <motion.p
            className="text-gray-300 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {mode === "login" && "Sign in to access your annotation workspace"}
            {mode === "register" && "Create a new account"}
            {mode === "reset" && "Reset your password"}
          </motion.p>
        </div>

        <motion.div
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
        >
          {mode !== "reset" && (
            <div className="flex items-center justify-center mb-6">
              <div className="bg-white/20 rounded-lg p-1 flex">
                <button
                  type="button"
                  onClick={() => mode !== "login" && switchMode("login")}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                    mode === "login"
                      ? "bg-indigo-500 text-white shadow-lg"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => mode !== "register" && switchMode("register")}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                    mode === "register"
                      ? "bg-indigo-500 text-white shadow-lg"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  Register
                </button>
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {mode === "login" && (
              <motion.form
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleLoginSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label htmlFor="password" className="block text-white text-sm font-medium">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => switchMode("reset")}
                      className="text-indigo-300 hover:text-indigo-200 text-xs underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="Enter your password"
                    required
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-300 text-sm bg-red-500/20 p-3 rounded-lg border border-red-500/50"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 py-3 px-6 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all duration-200 border border-white/30"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 py-3 px-6 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </div>
              </motion.form>
            )}

            {mode === "register" && (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleRegisterSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="reg-username" className="block text-white text-sm font-medium mb-2">
                    Display name
                  </label>
                  <input
                    type="text"
                    id="reg-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="How should we address you?"
                    minLength="3"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="reg-email" className="block text-white text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="reg-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="reg-password" className="block text-white text-sm font-medium mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="reg-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="Create a strong password"
                    required
                  />
                  {/* Live password checklist — ticks light up green as each
                      rule passes, the submit button stays disabled until all
                      three pass and the confirmation matches. */}
                  <ul className="mt-3 space-y-1.5">
                    {passwordChecks.map((c) => (
                      <li
                        key={c.id}
                        className={`flex items-center gap-2 text-xs transition-colors ${
                          c.passed ? "text-emerald-300" : "text-gray-400"
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                            c.passed
                              ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                              : "border-gray-500 text-transparent"
                          }`}
                          aria-hidden="true"
                        >
                          {/* Checkmark — only visible when rule is passed */}
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3">
                            <path d="M5 12l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-white text-sm font-medium mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="Confirm your password"
                    required
                  />
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="mt-2 text-xs text-amber-300">Passwords don't match yet.</p>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-300 text-sm bg-red-500/20 p-3 rounded-lg border border-red-500/50"
                  >
                    {error}
                  </motion.div>
                )}

                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-green-300 text-sm bg-green-500/20 p-3 rounded-lg border border-green-500/50"
                  >
                    {successMessage}
                  </motion.div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 py-3 px-6 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all duration-200 border border-white/30"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmitRegister}
                    className="flex-1 py-3 px-6 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Creating account...</span>
                      </div>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </div>
              </motion.form>
            )}

            {mode === "reset" && (
              <motion.form
                key="reset"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleResetSubmit}
                className="space-y-5"
              >
                <p className="text-gray-300 text-sm">
                  Enter the email address associated with your account and
                  we'll send you a link to reset your password.
                </p>

                <div>
                  <label htmlFor="reset-email" className="block text-white text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="reset-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-300 text-sm bg-red-500/20 p-3 rounded-lg border border-red-500/50"
                  >
                    {error}
                  </motion.div>
                )}

                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-green-300 text-sm bg-green-500/20 p-3 rounded-lg border border-green-500/50"
                  >
                    {successMessage}
                  </motion.div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="flex-1 py-3 px-6 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all duration-200 border border-white/30"
                  >
                    Back to sign in
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="flex-1 py-3 px-6 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </div>
                    ) : (
                      "Send reset link"
                    )}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <p className="text-gray-300 text-sm">
              Need help?{" "}
              <button
                type="button"
                onClick={() => navigate("/contact")}
                className="text-indigo-300 hover:text-indigo-200 underline"
              >
                Contact support
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
